import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import ReactPixel from 'react-facebook-pixel'
import ReactGA from 'react-ga4'
import { useDispatch, useSelector } from 'react-redux'
import { useUtilitySearch } from '../../../../hooks/address/useUtilitySearch'
import { AppDispatch, RootState } from '../../../../store'
import { WaterTestFormActions } from '../../../../store/waterTestForm'
import { CustomTextField } from '../../../CustomTextField'
import { StateDropdown } from '../StateDropdown'
import { SuccessMessage } from './SuccessMessage'

const PERSONAL_INFO_FIELDS = [
  { name: 'name', label: 'Full Name', placeholder: 'John Doe', type: 'text' },
  { name: 'email', label: 'Email', placeholder: 'john.doe@gmail.com', type: 'email' },
] as const

const ADDRESS_FIELDS = [
  { name: 'address1', label: 'Street name 1', placeholder: '123 Main St' },
  { name: 'address2', label: 'Street name 2', placeholder: 'Apt 123' },
] as const

interface ValidationError {
  field: string
  message: string
}

export const Form = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const { formData, loading, error, isSubmitted } = useSelector(
    (state: RootState) => state.waterTestForm,
  )
  const { utilities, isLoading: isLoadingUtilities } = useUtilitySearch()
  const getFieldError = (fieldName: string) => {
    if (!error?.errors) return null

    const fieldError = error.errors.find((err: ValidationError) => err.field === fieldName)
    return fieldError?.message
  }

  const handleInputChange =
    (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let value: string | boolean = e.currentTarget.value
      if (field === 'newsletter') {
        value = e.currentTarget.checked
        ReactPixel.trackCustom('click', { id: 'newsletter_option', value: e.currentTarget.value })
        ReactGA.event({
          action: 'click',
          category: 'User event tracking',
          label: 'Enable newsletter',
        })
      }
      dispatch(WaterTestFormActions.updateField({ field, value: value as any }))
    }

  const isFormValid = () => {
    const requiredFields: (keyof typeof formData)[] = [
      'name',
      'email',
      'address1',
      'city',
      'state',
      'zip',
      'waterSource',
      'equipment'
    ]

    return requiredFields.every((field) => {
      if (typeof requiredFields[field] === 'boolean') {return true}
      if( Array.isArray(formData[field])) {

        return  (formData[field] as string[]).length > 0
      }
      return (formData[field] as string).trim() !== ''
    })
  }

  const handleSubmit = async () => {
    ReactPixel.trackCustom('click', { id: 'submit_form_free_strip', name: 'Submit Form' })
    ReactGA.event({
      action: 'click',
      category: 'User event tracking',
      label: 'Form - Free Strip Submit',
    })

    const submission = await dispatch(WaterTestFormActions.submitWaterTestFormAsync())
    if (WaterTestFormActions.submitWaterTestFormAsync.fulfilled.match(submission)) {
      window.location.href = 'https://www.sippsafely.com/success-pages/test-strip'
    }
  }

  const handleUtilityChange = (e: any) => {
    const selectedUtility = utilities.find((util) => util.PWSID === e.target.value)

    if (selectedUtility) {
      dispatch(
        WaterTestFormActions.updateField({
          field: 'utility',
          value: selectedUtility.PWSID,
        }),
      )
    }
  }
  const handleHomeEquipmentChange = (e: any) => {

    let equipment = formData.equipment || []
    if (!e.target.checked) {
      equipment = formData.equipment.filter((item: string) => item !== e.target.name)
    } else {
      equipment = [...formData.equipment, e.target.name]
    }
    dispatch(
      WaterTestFormActions.updateField({
        field: 'equipment',
        value: equipment,
      }),
    )
  }
  if (isSubmitted) {
    return <SuccessMessage />
  }

  return (
    <Stack
      px={3}
      py={4}
      id="waterkit-req-form"
      width={{ xs: 'auto' }}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        color: theme.palette.primary.dark,
      }}
    >
      <Typography variant="h4" textAlign={{ md: 'left', xs: 'center' }} sx={{ pb: 0 }}>
        Request your free water test kit
      </Typography>
      <Typography variant="body1" sx={{ pb: 3 }}>
        Join the water safety movement in your community
      </Typography>
      {error && (
        <Typography color="error" fontSize="0.875rem" fontWeight={500}>
          {error?.message}
        </Typography>
      )}

      <Stack spacing={2} width="100%" textAlign="left">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
          {PERSONAL_INFO_FIELDS.map(({ name, label, placeholder, type }) => (
            <CustomTextField
              key={name}
              label={label}
              name={name}
              type={type}
              value={formData[name]}
              onChange={handleInputChange(name as keyof typeof formData)}
              placeholder={placeholder}
              fullWidth
              required
              error={!!getFieldError(name)}
              helperText={getFieldError(name)}
            />
          ))}
        </Stack>

        <Stack direction={'column'} spacing={2.5}>
          {ADDRESS_FIELDS.map(({ name, label, placeholder }) => (
            <CustomTextField
              key={name}
              label={label}
              name={name}
              value={formData[name]}
              onChange={handleInputChange(name as keyof typeof formData)}
              placeholder={placeholder}
              fullWidth
              required={name === 'address1'}
              error={!!getFieldError(name)}
              helperText={getFieldError(name)}
            />
          ))}

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={'end'}>
            <CustomTextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleInputChange('city')}
              fullWidth
              placeholder="New York"
              required
              error={!!getFieldError('city')}
              helperText={getFieldError('city')}
            />
            <StateDropdown
              label="State"
              value={formData.state}
              onChange={(value) =>
                dispatch(WaterTestFormActions.updateField({ field: 'state', value }))
              }
            />
            <CustomTextField
              label="ZIP Code"
              name="zip"
              value={formData.zip}
              onChange={handleInputChange('zip')}
              fullWidth
              placeholder="Enter your ZIP code"
              required
              error={!!getFieldError('zip')}
              helperText={getFieldError('zip')}
            />
          </Stack>
        </Stack>

        <Stack>
          <Typography fontSize={'0.875rem'} fontWeight={600} color={theme.palette.primary.dark}>
            What's your water source?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} mt={1} width="100%">
            {[
              {
                value: 'public',
                label: ['Public', 'water system'],
              },
              {
                value: 'well',
                label: ['Well', 'water'],
              },
            ].map(({ value, label }) => {
              const isActive = formData.waterSource === value

              return (
                <Stack
                  key={value}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    border: `1px solid ${isActive ? theme.palette.primary.dark : '#E2E8F0'}`,
                    background: isActive ? '#E6EEF4' : '#FFFFFF',
                    borderRadius: '8px',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.5, sm: 2.5 },
                    minWidth: { xs: '100%', sm: 160 },
                    width: { xs: '100%' },
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: isActive ? '0 2px 8px rgba(0,89,142,0.08)' : 'none',
                  }}
                  onClick={() =>
                    dispatch(WaterTestFormActions.updateField({ field: 'waterSource', value }))
                  }
                >
                  <SvgIcon sx={{ fontSize: { xs: 18, sm: 22 } }}>
                    {isActive ? (
                      <>
                        <circle
                          cx="11"
                          cy="11"
                          r="9"
                          fill="#fff"
                          stroke={theme.palette.button.default}
                          strokeWidth="2"
                        />
                        <circle cx="11" cy="11" r="5.8" fill={theme.palette.button.default} />
                      </>
                    ) : (
                      <circle cx="11" cy="11" r="9" fill="#fff" stroke="#A0AEC0" strokeWidth="2" />
                    )}
                  </SvgIcon>
                  <Typography fontWeight={500} color={theme.palette.grey[600]} lineHeight={1.2}>
                    {label.join(' ')}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
        <Typography variant="h6" fontSize={{ xs: '14px', sm: '15px' }}>
          Existing in home equipment
        </Typography>
        <FormGroup
          sx={{ display: 'flex', flexDirection: 'row' }}
          onChange={handleHomeEquipmentChange}
        >
          {['Water pitcher', 'Refrigerator', 'Under sink', 'Counter Top', 'Whole Home', 'None'].map(
            (item) => {

              return (
                <FormControlLabel
                  key={item}
                  name={item}
                  checked={formData.equipment.includes(item) }
                  control={<Checkbox />}
                  sx={{ minWidth: '200px' }}
                  label={item}
                />
              )
            },
          )}
        </FormGroup>

        {/* Utility Dropdown with Tooltip */}
        <Stack spacing={1} width="100%">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              fontWeight={500}
              color={theme.palette.grey[700]}
              fontSize={{ xs: '14px', sm: '15px' }}
            >
              Select utility
            </Typography>
            <Tooltip
              title={
                <>
                  <Typography fontWeight={600} fontSize="14px" color="#fff" mb={0.5}>
                    Why do we ask this?
                  </Typography>
                  <Typography fontSize="14px" fontWeight={500} color="#D0D5DD">
                    This helps compare and contrast the water quality data from your utility to the
                    data in your home. It will improve accuracy and provide greater context to the
                    report.
                  </Typography>
                </>
              }
              arrow
              placement="top"
              slotProps={{
                popper: { sx: { zIndex: 1500 } },
                tooltip: {
                  sx: {
                    background: '#101928',
                    borderRadius: '8px',
                    p: 2,
                    width: { xs: '90vw', sm: '340px' },
                    maxWidth: { xs: '90vw', sm: '340px' },
                  },
                },
                arrow: {
                  sx: {
                    color: '#101928',
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{ fontSize: 18, color: theme.palette.grey[500], cursor: 'pointer' }}
              />
            </Tooltip>
          </Stack>
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={formData.utility || ''}
              onChange={handleUtilityChange}
              sx={{
                background: '#fff',
                borderRadius: 2,
                fontSize: { xs: '14px', sm: '15px' },
                color: theme.palette.grey[400],
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E2E8F0',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.dark,
                },
                minHeight: 48,
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: theme.palette.grey[400] }}>
                      {isLoadingUtilities ? 'Loading utilities...' : 'Select utility'}
                    </span>
                  )
                }
                const selectedUtility = utilities.find((util) => util.PWSID === selected)
                return selectedUtility ? selectedUtility.PWS_NAME : ''
              }}
            >
              {isLoadingUtilities ? (
                <MenuItem disabled>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    width="100%"
                    justifyContent="center"
                  >
                    <CircularProgress size={20} />
                    <Typography>Loading utilities...</Typography>
                  </Stack>
                </MenuItem>
              ) : (
                utilities.map((utility) => (
                  <MenuItem key={utility._id} value={utility.PWSID}>
                    <Stack>
                      <Typography variant="body1">{utility.PWS_NAME}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {utility.CITY_NAME}, {utility.STATE_CODE} • {utility.PWS_TYPE_CODE}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  name="newsletter"
                  onChange={handleInputChange('newsletter')}
                  sx={{ pt: 0 }}
                />
              }
              label={
                <Stack flexDirection="column" sx={{ mt: 1 }}>
                  <Typography>Opt-in for our newsletter</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                    Stay up to date on our upcoming product launch
                  </Typography>
                </Stack>
              }
            />
          </FormControl>
        </Stack>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid() || loading}
          sx={{
            mt: 2,
            px: 4,
            py: 1.5,
          }}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </Stack>
    </Stack>
  )
}
